import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Envelope, MagnifyingGlass, Check, Trash, Clock, User, EnvelopeOpen
} from '@phosphor-icons/react';

export default function CmsContactMessages() {
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      // Mock data - in real implementation, fetch from backend
      // const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contact-messages`, { withCredentials: true });
      
      // Mock messages for demonstration
      const mockMessages = [
        {
          id: '1',
          name: 'राज कुमार',
          email: 'raj@example.com',
          subject: 'कविता प्रकाशन के बारे में',
          message: 'मैं अपनी कविताएं प्रकाशित करना चाहता हूं। कृपया प्रक्रिया बताएं।',
          status: 'new',
          created_at: new Date().toISOString()
        },
        {
          id: '2',
          name: 'प्रिया शर्मा',
          email: 'priya@example.com',
          subject: 'तकनीकी समस्या',
          message: 'वेबसाइट पर लॉगिन करने में समस्या आ रही है।',
          status: 'read',
          created_at: new Date(Date.now() - 86400000).toISOString()
        },
        {
          id: '3',
          name: 'अमित वर्मा',
          email: 'amit@example.com',
          subject: 'सहयोग की पेशकश',
          message: 'मैं एक संपादक हूं और आपके प्लेटफॉर्म के साथ काम करना चाहता हूं।',
          status: 'replied',
          created_at: new Date(Date.now() - 172800000).toISOString()
        }
      ];
      
      setMessages(mockMessages);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (messageId) => {
    try {
      // await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contact-messages/${messageId}/read`, {}, { withCredentials: true });
      setMessages(messages.map(m => m.id === messageId ? { ...m, status: 'read' } : m));
      toast.success('Marked as read');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    
    try {
      // await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contact-messages/${messageId}`, { withCredentials: true });
      setMessages(messages.filter(m => m.id !== messageId));
      toast.success('Message deleted');
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const filteredMessages = messages.filter(msg => {
    const matchesSearch = msg.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         msg.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         msg.subject?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || msg.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: messages.length,
    new: messages.filter(m => m.status === 'new').length,
    read: messages.filter(m => m.status === 'read').length,
    replied: messages.filter(m => m.status === 'replied').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Envelope size={32} weight="fill" className="text-primary" />
          Contact Messages
        </h1>
        <p className="text-muted-foreground mt-1">
          View and manage contact form submissions
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="text-sm text-muted-foreground">Total</div>
          <div className="text-2xl font-bold">{stats.total}</div>
        </div>
        <div className="bg-card border border-yellow-500/30 rounded-lg p-4">
          <div className="text-sm text-yellow-600 dark:text-yellow-400">New</div>
          <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats.new}</div>
        </div>
        <div className="bg-card border border-blue-500/30 rounded-lg p-4">
          <div className="text-sm text-blue-600 dark:text-blue-400">Read</div>
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.read}</div>
        </div>
        <div className="bg-card border border-green-500/30 rounded-lg p-4">
          <div className="text-sm text-green-600 dark:text-green-400">Replied</div>
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.replied}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search messages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg"
            />
          </div>

          <div className="flex gap-2">
            {['all', 'new', 'read', 'replied'].map(status => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === status
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted hover:bg-muted/80'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Messages List */}
      <div className="space-y-3">
        {filteredMessages.map(message => (
          <div
            key={message.id}
            className={`bg-card border rounded-lg p-4 hover:shadow-md transition-shadow ${
              message.status === 'new' ? 'border-yellow-500/50' : 'border-border'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <User size={20} className="text-muted-foreground" />
                  <span className="font-semibold">{message.name}</span>
                  <span className="text-sm text-muted-foreground">{message.email}</span>
                  <span className={`px-2 py-1 text-xs rounded ${
                    message.status === 'new' ? 'bg-yellow-500/20 text-yellow-600' :
                    message.status === 'read' ? 'bg-blue-500/20 text-blue-600' :
                    'bg-green-500/20 text-green-600'
                  }`}>
                    {message.status}
                  </span>
                </div>
                <h3 className="font-medium mb-2">{message.subject}</h3>
                <p className="text-sm text-muted-foreground mb-2">{message.message}</p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Clock size={14} />
                  {new Date(message.created_at).toLocaleString()}
                </div>
              </div>

              <div className="flex gap-2 ml-4">
                {message.status === 'new' && (
                  <button
                    onClick={() => handleMarkAsRead(message.id)}
                    className="p-2 hover:bg-muted rounded-lg transition-colors"
                    title="Mark as read"
                  >
                    <EnvelopeOpen size={18} />
                  </button>
                )}
                <button
                  onClick={() => handleDelete(message.id)}
                  className="p-2 hover:bg-red-500/10 text-red-500 rounded-lg transition-colors"
                  title="Delete"
                >
                  <Trash size={18} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredMessages.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          No messages found
        </div>
      )}
    </div>
  );
}
